/**
 * Creates a GeoJSON circle polygon around a center point
 *
 * @param center - Center coordinates [latitude, longitude]
 * @param radiusInMeters - Radius of the circle in meters
 * @param points - Number of points to use for the circle (default: 64)
 * @returns GeoJSON FeatureCollection with a circle polygon
 */
export const createCircleGeoJSON = ({
  center,
  radiusInMeters,
  points = 64,
}: {
  center: [number, number];
  radiusInMeters: number;
  points?: number;
}): GeoJSON.FeatureCollection => {
  const [lat, lng] = center;
  const coordinates: [number, number][] = [];

  // Earth's radius in meters
  const earthRadius = 6371000;

  // Convert radius to radians
  const radiusInRadians = radiusInMeters / earthRadius;

  // Generate circle points
  for (let i = 0; i <= points; i++) {
    const angle = (i * 360) / points;
    const angleRadians = (angle * Math.PI) / 180;

    // Calculate point coordinates using spherical geometry
    const latRadians = (lat * Math.PI) / 180;
    const lngRadians = (lng * Math.PI) / 180;

    const newLatRadians = Math.asin(
      Math.sin(latRadians) * Math.cos(radiusInRadians) +
        Math.cos(latRadians) *
          Math.sin(radiusInRadians) *
          Math.cos(angleRadians),
    );

    const newLngRadians =
      lngRadians +
      Math.atan2(
        Math.sin(angleRadians) *
          Math.sin(radiusInRadians) *
          Math.cos(latRadians),
        Math.cos(radiusInRadians) -
          Math.sin(latRadians) * Math.sin(newLatRadians),
      );

    const newLat = (newLatRadians * 180) / Math.PI;
    const newLng = (newLngRadians * 180) / Math.PI;

    coordinates.push([newLng, newLat]);
  }

  return {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        properties: {
          radius: radiusInMeters,
        },
        geometry: {
          type: "Polygon",
          coordinates: [coordinates],
        },
      },
    ],
  };
};
